import { AssetType, InvoiceStatus, LoanStatus } from '@/constants/asset';
import {
  useGetPoolLoanAssets,
  useGetPoolNoteTokenAssets,
} from '@/services/api/pool.service';
import { securitizationPoolValueService } from '@/services/balancer/contracts/contracts/securitization-pool-value-service';
import _ from 'lodash';
import {
  getExtraInfoFromAssets,
  getExtraInfoFromNoteTokenAssets,
} from './untangled';

export const calculateApy = function (poolEvent, targetTime = new Date()) {
  const yieldValue = _.get(poolEvent, 'yieldValue', 0);
  if (yieldValue) return yieldValue;
  const eventSaleType = _.get(poolEvent, 'eventSaleType', null);
  if (eventSaleType === null) return 0;
  const SIMPLE_SUBSCRIPTION = 4;
  const isSimpleSubscription = eventSaleType === SIMPLE_SUBSCRIPTION;
  const DIRECT_ALLOCATION = 1;
  const isDirectAllocation = eventSaleType === DIRECT_ALLOCATION;

  if (
    poolEvent.eventSaleType === null ||
    isSimpleSubscription ||
    isDirectAllocation ||
    poolEvent.status === 2
  )
    return poolEvent.apy;

  // if (poolEvent.apy) return poolEvent.apy; // SIMPLE_SUBSCRIPTION

  const interval = getInterval(poolEvent, targetTime);

  const priceChange = poolEvent.priceChangePerInterval * interval;

  return Math.min(poolEvent.reservedAPY, poolEvent.startAPY + priceChange || 0);
};

export const getInterval = function (poolEvent, targetTime = new Date()) {
  const timeDiff = Math.max(targetTime.getTime() - poolEvent.startTime, 0);
  const eventPassingDays = timeDiff / 1000 / 60 / 60 / 24;
  const interval = _.floor(eventPassingDays / poolEvent.timeInterval);
  return interval;
};

export const getPoolActivityLabel = (
  activity: any,
  ticker: string | undefined
) => {
  switch (activity.transactionType) {
    case 1:
      return `Loan repayment - #${
        activity.transaction?.content?.loanIdentifier || ''
      }`;
    case 3:
      return `Invoice repayment - #${
        activity.transaction?.content?.openItemId || ''
      }`;
    case 4:
      return 'Pool setup';
    case 5:
      return 'Tokenize assets';
    case 6:
      return 'Transfer assets to pool';
    case 7:
      return 'Change of control';
    case 8:
      return 'Issuance of note';
    case 9:
      return 'Issuance of junior note';
    case 10:
      return 'Import assets';
    case 11:
      return 'Export assets';
    case 'SOT_PURCHASE':
      return `Invest ${ticker}_SOT`;
    case 'JOT_PURCHASE':
      return `Invest ${ticker}_JOT`;
    case 'DRAWDOWN':
      return 'Drawdown';
    case 'REPAY':
      return 'Repay';
    case 'EPOCH_WITHDRAW':
      return 'Epoch withdraw';
    case 2:
    default:
      return 'Note redeemed';
  }
};

export const getVaultPoolActivityLabel = (activity: any) => {
  switch (activity.transactionType) {
    case 'VAULT_WITHDRAW':
      return `Vault Withdraw`;
    case 'VAULT_DEPOSIT':
      return 'Vault deposit';
    case 'MANAGER_DRAWDOWN':
      return 'Withdraw (by manager)';
    case 'MANAGER_INVEST':
      return 'Invest (by manager)';
    default:
      return 'Note redeemed';
  }
};

export const getExtraInfo = async pool => {
  let assetData;
  let balances;
  let assetInterestRates;
  let extraInfo;

  if (pool.assetType == AssetType.LENDING) {
    const { data } = await useGetPoolLoanAssets(pool.id);
    //isLoanAssets
    assetData = (data as any).value?.rows;
    balances = await securitizationPoolValueService.getLoanAssetsValues(
      assetData,
      pool.poolContractAddress,
      pool?.currency?.decimals
    );
    assetInterestRates =
      await securitizationPoolValueService.getLoanInterestRates(
        pool.poolContractAddress,
        assetData
      );

    const assets = (assetData || []).map((asset, idx) => {
      let outstandingPrincipalAmount = asset.outstandingPrincipalAmount;
      let interestRatePercentage = asset.interestRatePercentage;
      if (asset.tokenId) {
        outstandingPrincipalAmount = balances[idx] || 0;
        interestRatePercentage = assetInterestRates[idx] || 0;
      }
      return {
        ...asset,
        outstandingPrincipalAmount,
        interestRatePercentage,
      };
    });

    extraInfo = getExtraInfoFromAssets(pool, assets);
  } else {
    // assetData = (await useGetPoolNoteTokenAssets(pool.id)).data.value;
    assetData = [];
    extraInfo = getExtraInfoFromNoteTokenAssets(assetData);
    balances =
      await securitizationPoolValueService.getMultiNoteTokenAssetsBalance(
        assetData
      );
  }
  extraInfo.assetOutstanding = `${
    assetData.filter((asset, idx) => {
      if (pool.assetType == AssetType.LENDING)
        return (
          asset.issuanceCommitmentHash && asset.status !== LoanStatus.REPAID
        );
      else if (pool.assetType == AssetType.INVOICE)
        return asset.aitTokenId && asset.status !== InvoiceStatus.PAID;
      else return balances[idx] && balances[idx].principal > 0;
    }).length
  } /  ${assetData.length}`;
  return extraInfo;
};

export const viewState = (state: any) => {
  return JSON.parse(JSON.stringify(state));
};
